// frontend/src/components/MainContent/MainContent.js

import React, { useContext, useState, useRef, useEffect } from 'react';
import { TemplateContext } from "../../context/TemplateContext";
import { UserContext } from "../../UserContext";
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getPatient } from '../Calendar/calendarApi';
import Timer from '../Timer/Timer';
import TranscriptionArea from '../TranscriptionArea/TranscriptionArea';
import JournalEditor from '../JournalEditor/JournalEditor';
import TopBar from '../TopBar/TopBar';
import ImageGallery from '../ImageGallery/ImageGallery';
import SessionNameField from '../SessionNameField/SessionNameField';
import TranscriptionPendingModal from '../TranscriptionPendingModal/TranscriptionPendingModal';
import PatientSelector from '../PatientSelector/PatientSelector';
import { useRecording } from '../../hooks/useRecording';
import { useAudioStreaming } from '../../hooks/useAudioStreaming';
import { useAudioLevel } from '../../hooks/useAudioLevel';
import { useTranscription } from '../../hooks/useTranscription';
import { useRecordingError } from '../../hooks/useRecordingError';
import { useMicrophones } from '../../hooks/useMicrophones';
import { useSession } from '../../hooks/useSession';
import { useLocalRecording } from '../../hooks/useLocalRecording';
import { getAllSessions, deleteSessionAndChunks } from '../../services/localRecording/db';
import { acquireRecordingLock, releaseRecordingLock, isRecordingElsewhere } from '../../utils/recordingLock';
import apiClient, { getChunkStatus, subscribeToSessionStatus } from '../../apiClient';
import streamClient from '../../streamClient';
import './MainContent.css';

const CHUNK_START_AFTER = 30;
const CHUNK_INTERVAL = 30;

const isElectron = typeof window !== 'undefined' && !!window.aionDesktop;

// Besked när en andra inspelning stoppas (en inspelning åt gången per användare).
const NOTICE_OTHER_WINDOW =
  'Du spelar redan in i ett annat fönster. Avsluta den inspelningen innan du startar en ny.';
const NOTICE_OTHER_DEVICE =
  'Du har redan en inspelning igång i ett annat fönster eller på en annan enhet. Avsluta den där innan du startar en ny. Har den avbrutits går det att starta igen om några minuter.';
const NOTICE_SESSION_ELSEWHERE =
  'Den här inspelningen pågår i ett annat fönster eller på en annan enhet. Fortsätt och avsluta den där.';
const NOTICE_INTERRUPTED =
  'Inspelningen avbröts. Tryck på Återuppta inspelning för att fortsätta spela in.';

function MainContent() {
  // --- URL query params ---
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionIdFromURL = searchParams.get('session_id') || null;
  const completedParam = searchParams.get('completed') === '1';

  // Nya URL-params för patient-initierad inspelning
  const patientIdFromURL = searchParams.get('patient_id');
  const templateIdFromURL = searchParams.get('template_id');
  const appointmentIdFromURL = searchParams.get('appointment_id');
  const autoRecord = searchParams.get('auto_record') === '1';

  // Hooks
  const recording = useRecording();
  const { transcription, transcriptionRef, isTranscribing, transcribeChunk, resetTranscription, setTranscriptionFromData, appendText } = useTranscription();
  const { error, handleError, clearError } = useRecordingError();
  const { microphones, selectedMicId, setSelectedMicId, captureSystemAudio, setCaptureSystemAudio } = useMicrophones(isElectron);
  const { sessionId, setSessionId, updateSessionStatus, fetchSessionData } = useSession(sessionIdFromURL, completedParam, 'session');

  // Lokal-först-inspelning: chunkar sparas i IndexedDB och laddas upp i
  // bakgrunden (raderas först vid server-kvittens). Bekräftad transkript-text
  // matas in i transcriptionRef via appendText; server-session-id (som kan komma
  // sent om servern var nere vid start) sätts när uppladdaren skapat sessionen.
  const [clientSessionId, setClientSessionId] = useState(null);
  const {
    beginSession: beginLocalSession,
    attachServerSession,
    enqueueChunk,
    finishSession: finishLocalSession,
    setSessionMeta: setLocalSessionMeta,
    pendingCounts,
  } = useLocalRecording({
    // Bara text från DEN HÄR inspelningen. Uppladdaren delas av alla flikar och
    // kan skicka ikapp en äldre inspelning medan en ny pågår.
    onConfirmedText: (ev) => { if (ev.text && ev.clientSessionId === clientSessionId) appendText(ev.text); },
    onServerSession: ({ serverSessionId }) => { if (serverSessionId) setSessionId(serverSessionId); },
    // Bara DEN HÄR inspelningen: annars kan en äldre inspelning som skickas ikapp byta
    // aktiv session mitt i nästa patient, och journalen sparas då på fel session.
    onSessionCompleted: ({ clientSessionId: completedId, serverSessionId }) => {
      if (serverSessionId && completedId === clientSessionId) setSessionId(serverSessionId);
    },
  });
  // Lokala, ännu ej bekräftade chunkar för aktiv session (blockerar journal).
  const localPendingForSession = clientSessionId
    ? (pendingCounts[clientSessionId]?.pending || 0)
    : 0;

  // (Effekten som synkar lokal sessions mall/namn är flyttad längre ned — den
  //  beror på selectedTemplate/templateId/sessionName som deklareras senare.)

  // Routar en chunk rätt: nya inspelningar går via lokal-först-kön (enqueueChunk);
  // en återupptagen BEFINTLIG server-session (öppnad från Mina sessioner) saknar
  // lokal session och laddas upp direkt mot sitt kända server-id (som förut) —
  // detta undviker både datatapp och chunk_index-krock mot redan mottagna chunkar.
  const handleChunk = async (blob, isFinal = false) => {
    if (!blob) return;
    if (clientSessionId) {
      await enqueueChunk(blob, isFinal);
    } else if (sessionId) {
      await transcribeChunk(blob, sessionId, '/recorder/sessions', isFinal);
    }
  };
  
  const { soundLevel, noAudioDetected } = useAudioLevel({
    isRecording: recording.isRecording,
    isPaused: recording.isPaused,
    selectedMicId,
  });

  const { transcriptionAreaRef } = useAudioStreaming({
    currentTime: recording.currentTime,
    isRecording: recording.isRecording,
    isPaused: recording.isPaused,
    chunkStartAfter: CHUNK_START_AFTER,
    chunkInterval: CHUNK_INTERVAL
  });

  // GPT-sektioner
  const [streamingSections, setStreamingSections] = useState([]);
  const [generatedSections, setGeneratedSections] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  // Templates
  const { templateId, setTemplateId } = useContext(TemplateContext);
  const { user } = useContext(UserContext);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // Vilket öppnat möte som hunnit laddas (då är dess mall känd, se startmallen nedan).
  const [loadedSessionId, setLoadedSessionId] = useState(null);
  // Alltid färsk referens till vald mall. Sätts även SYNKRONT vid mallbyte
  // (se handleChangeTemplate) så att regenerering aldrig kan läsa en inaktuell
  // selectedTemplate och skicka fel template_id till backend.
  const selectedTemplateRef = useRef(null);
  useEffect(() => { selectedTemplateRef.current = selectedTemplate; }, [selectedTemplate]);
  // Vid mallbyte: håller mall-id:t vi vill regenerera mot. Regenereringen körs
  // i en effekt FÖRST när selectedTemplate hunnit uppdateras till den nya mallen
  // (samma väg som knappen "Gör om journal", som bevisligen fungerar).
  const [regenerateForTemplateId, setRegenerateForTemplateId] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // Visa JournalEditor eller ej?
  const [showJournal, setShowJournal] = useState(false);
  const [transcriptionUpdatedAfterJournal, setTranscriptionUpdatedAfterJournal] = useState(false);

  // Loading overlay för journal generation
  const [showGenerationOverlay, setShowGenerationOverlay] = useState(false);
  const [generationMessage, setGenerationMessage] = useState('');

  // Humla ImageGallery state
  const [showImageGallery, setShowImageGallery] = useState(false);

  // Session name state
  const [sessionName, setSessionName] = useState('');

  // Håll den lokala sessionens mall/namn färska om användaren väljer mall eller
  // namnger sessionen EFTER inspelningsstart (så en lazy-skapad server-session
  // får rätt mall även när servern var nere vid start). Placerad EFTER
  // selectedTemplate/templateId/sessionName så deras deklarationer hunnit köra.
  useEffect(() => {
    if (!clientSessionId) return;
    const tid = selectedTemplate?.template_id || templateId || null;
    setLocalSessionMeta({ templateId: tid, sessionName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSessionId, selectedTemplate, templateId, sessionName]);

  // Pending journal text from loaded session (väntar på template)
  const [pendingJournalText, setPendingJournalText] = useState(null);

  // Pending transcription modal state
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [backgroundRetryActive, setBackgroundRetryActive] = useState(false);
  // Lokal-först: sant när servern var nere så länge att inspelningen ännu inte
  // kunnat skickas — den ligger säkert lokalt och skickas automatiskt.
  const [localDeferredNotice, setLocalDeferredNotice] = useState(false);
  const retryCleanupRef = useRef(null);

  // Klinik-inställningar för patientjournal
  const [clinicSettings, setClinicSettings] = useState({
    use_internal_journal: false,
    journal_system: 'Kaddio'
  });
  const [selectedPatient, setSelectedPatient] = useState(null);

  // State för kopplad bokning (från URL)
  const [linkedAppointmentId, setLinkedAppointmentId] = useState(null);

  // Ref för att förhindra att auto-record körs flera gånger
  const autoRecordStartedRef = useRef(false);

  // En inspelning åt gången: spärr mot dubbelstart i samma fönster, besked när
  // en andra inspelning stoppas, och läge där sessionen spelas in någon annanstans.
  const startingRef = useRef(false);
  const [isStarting, setIsStarting] = useState(false);
  const [recordingNotice, setRecordingNotice] = useState(null);
  const [recordingElsewhere, setRecordingElsewhere] = useState(false);

  // ===========================================================
  //        Hämta klinik-inställningar
  // ===========================================================
  useEffect(() => {
    const fetchClinicSettings = async () => {
      try {
        const resp = await apiClient.get('/api/journal/clinic/settings');
        setClinicSettings({
          use_internal_journal: resp.data.use_internal_journal || false,
          journal_system: resp.data.journal_system || 'Kaddio'
        });
      } catch (error) {
        console.error('Error fetching clinic settings:', error);
        // Använd defaults om hämtningen misslyckas
      }
    };

    fetchClinicSettings();
  }, []);

  // ===========================================================
  //        Ladda patient från URL (för patient-initierad inspelning)
  // ===========================================================
  useEffect(() => {
    const loadPatientFromURL = async () => {
      if (patientIdFromURL && !selectedPatient) {
        try {
          const patient = await getPatient(patientIdFromURL);
          setSelectedPatient(patient);

          // Sätt appointment_id om det skickades med
          if (appointmentIdFromURL) {
            setLinkedAppointmentId(parseInt(appointmentIdFromURL));
          }

          console.log('Patient loaded from URL:', patient.firstname, patient.lastname);
        } catch (error) {
          console.error('Error loading patient from URL:', error);
        }
      }
    };

    loadPatientFromURL();
  }, [patientIdFromURL, appointmentIdFromURL]);

  // ===========================================================
  //        Hämta mallar
  // ===========================================================
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const resp = await apiClient.get('/api/journalmallar');

        if (Array.isArray(resp.data)) {
          // Vilken mall som väljs avgörs i effekterna nedan: här är templateId
          // fastfruset från när sidan öppnades och kan skriva över mötets mall.
          setTemplates(resp.data);
        } else {
          handleError('Fel vid hämtning av mallar.');
        }

        setLoadingTemplates(false);
      } catch (err) {
        handleError('Kunde inte hämta mallar', err);
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, []);


  // ===========================================================
  //        Startmall: användarens standardmall, annars första
  // ===========================================================
  // Används för nya möten, och för öppnade möten vars mall saknas eller inte finns
  // i listan. Ett öppnat möte har annars sin egen mall (handleFetchSessionData) och
  // ska inte få startmallen medan det laddas.
  useEffect(() => {
    if (templates.length === 0) return;
    if (templateId && templates.some(t => t.template_id === templateId)) return;
    if (sessionIdFromURL && loadedSessionId !== sessionIdFromURL) return;
    const start = templates.find(t => t.template_id === user?.default_journaltemplate_id) || templates[0];
    setTemplateId(start.template_id);
    setSelectedTemplate(start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, templateId, sessionIdFromURL, loadedSessionId, user]);

  // ===========================================================
  //     Synka templateId (context) → selectedTemplate
  // ===========================================================
  useEffect(() => {
    if (!templateId || templates.length === 0) return;
    const found = templates.find(t => t.template_id === templateId);
    if (found) setSelectedTemplate(found);
  }, [templateId, templates]);

  // ===========================================================
  //     Sätt template från URL (för patient-initierad inspelning)
  // ===========================================================
  useEffect(() => {
    if (templateIdFromURL && templates.length > 0 && !sessionIdFromURL) {
      const urlTemplateId = parseInt(templateIdFromURL);
      const found = templates.find(t => t.template_id === urlTemplateId);
      if (found && found.template_id !== selectedTemplate?.template_id) {
        setTemplateId(urlTemplateId);
        setSelectedTemplate(found);
        console.log('Template set from URL:', found.template_name);
      }
    }
  }, [templateIdFromURL, templates, sessionIdFromURL]);

  // ===========================================================
  //     Auto-starta inspelning (för patient-initierad inspelning)
  // ===========================================================
  useEffect(() => {
    // Kontrollera alla förutsättningar för auto-start
    const canAutoStart = autoRecord &&
      !autoRecordStartedRef.current &&
      !recording.isRecording &&
      !sessionId &&
      selectedTemplate &&
      selectedPatient;

    if (canAutoStart) {
      // Markera att vi redan startat för att undvika dubbel-start
      autoRecordStartedRef.current = true;
      console.log('Auto-starting recording for patient:', selectedPatient.firstname, selectedPatient.lastname);
      startRecording();
    }
  }, [autoRecord, recording.isRecording, sessionId, selectedTemplate, selectedPatient]);

  // ===========================================================
  //        Hämta session-data om sessionId finns
  // ===========================================================
  useEffect(() => {
    // Besked från en tidigare öppnad session ska inte hänga kvar.
    setRecordingElsewhere(false);
    setRecordingNotice(null);
    if (sessionIdFromURL) {
      handleFetchSessionData(sessionIdFromURL);
    }
    // eslint-disable-next-line
  }, [sessionIdFromURL]);

  // Sessionen spelas in någon annanstans: kolla med jämna mellanrum. När den
  // avslutats eller slutat skicka ljud laddas den om (visas då klar eller pausad).
  useEffect(() => {
    if (!recordingElsewhere || !sessionId) return;
    const timer = setInterval(async () => {
      try {
        const data = await fetchSessionData(sessionId);
        if (data.status === 'in_progress' && (data.recording_active || await isRecordingElsewhere())) return;
        setRecordingElsewhere(false);
        setRecordingNotice(null);
        handleFetchSessionData(sessionId);
      } catch (_) { /* servern nere: försök igen nästa varv */ }
    }, 20000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [recordingElsewhere, sessionId]);

  // ===========================================================
  //   Parsa pending journal när template är redo
  // ===========================================================
  useEffect(() => {
    // Vänta tills både journaltext och template finns
    if (!pendingJournalText || !selectedTemplate) return;

    // VIKTIGT: selectedTemplate synkas från templateId i en separat effekt (en
    // render senare). Vid laddning av session sätts templateId + pendingJournalText
    // i samma batch, så vid första rendret är selectedTemplate fortfarande den
    // gamla (standard)mallen. Parsar vi då hamnar sessionens rubriker utanför
    // allowedTitles och klistras ihop under en rubrik. Vänta tills selectedTemplate
    // faktiskt motsvarar aktuellt templateId innan vi parsar.
    if (selectedTemplate.template_id !== templateId) return;

    console.log('Parsing pending journal with template:', selectedTemplate.template_name);
    const secs = parseJournalNote(pendingJournalText);
    setGeneratedSections(secs);

    // Rensa pending text så vi inte parsar igen
    setPendingJournalText(null);
  }, [pendingJournalText, selectedTemplate, templateId]);

  const formatDateTime = (date) => {
    if (!date) return '';
    return date.toLocaleString('sv-SE');
  };

  const handleFetchSessionData = async (sid) => {
    console.log('handleFetchSessionData called with sessionId:', sid);
    try {
      const sessionData = await fetchSessionData(sid);
      const {
        status,
        transcription_text,
        generated_journal,
        start_time,
        elapsed_time,
        template_id,
        session_name,
        transcription_updated_after_journal,
        recording_active,
      } = sessionData;

      // Set session name if available
      if (session_name) {
        setSessionName(session_name);
      }
      
      console.log('Session data fetched:', {
        status, 
        elapsed_time, 
        transcription_text: transcription_text?.length,
        template_id,
        generated_journal: generated_journal?.length
      });


      // Saknar mötet mall får det startmallen, inte mallen från ett tidigare öppnat möte.
      setTemplateId(template_id || null);
      setLoadedSessionId(String(sid));

      setTranscriptionFromData(transcription_text);

      if (start_time && !recording.startDateTime) {
        recording.setStartDateTime(new Date(start_time));
      }

      if (status === 'completed' || completedParam) {
        if (elapsed_time) {
          // elapsed_time från backend är i sekunder
          recording.setElapsedTimeFromBackend(elapsed_time);
        }
        if (generated_journal) {
          // Spara journaltext och vänta på att template laddas innan vi parsar
          setPendingJournalText(generated_journal);
        }
        setTranscriptionUpdatedAfterJournal(transcription_updated_after_journal || false);
        setShowJournal(true);
        recording.stop();
      } else if (status === 'paused') {
        setShowJournal(false);
        console.log('Setting paused state with elapsed_time:', elapsed_time);
        // Sätt recording state till pausad med elapsed time
        recording.setPausedState(elapsed_time);
      } else if (status === 'in_progress') {
        setShowJournal(false);
        if (elapsed_time) {
          // elapsed_time från backend är i sekunder
          recording.setElapsedTimeFromBackend(elapsed_time);
        }
        // Ljudet tas upp i fönstret som startade inspelningen. Det här fönstret
        // ska varken låtsas spela in (timer + Pausa) eller börja spela in själv.
        if (recording_active || await isRecordingElsewhere()) {
          // Pågår i ett annat fönster/på en annan enhet → bara besked.
          setRecordingElsewhere(true);
          setRecordingNotice(NOTICE_SESSION_ELSEWHERE);
        } else {
          // Ingen spelar in den längre (fliken stängdes/laddades om utan avslut):
          // visa som pausad så användaren själv väljer att återuppta.
          recording.setPausedState(elapsed_time || 0);
          setRecordingNotice(NOTICE_INTERRUPTED);
          try {
            await updateSessionStatus('pause', (elapsed_time || 0) * 1000);
          } catch (err) {
            console.warn('Kunde inte markera avbruten session som pausad:', err);
          }
        }
      }
    } catch (err) {
      setLoadedSessionId(String(sid));
      handleError('Kunde inte hämta session', err);
    }
  };

  // ===========================================================
  //        Spara generated_journal
  // ===========================================================
  const saveGeneratedJournal = async (journalText) => {
    if (!sessionId) return;
    try {
      await apiClient.patch(`/recorder/sessions/${sessionId}/update_journal`, {
        generated_journal: journalText,
      });
      console.log('Journal sparad framgångsrikt!');
    } catch (err) {
      console.error('Kunde inte spara journal:', err);
    }
  };

  // ===========================================================
  //   Hantera patientväljare
  // ===========================================================
  // Patientvalet lever enbart i frontend-state — inspelningssessionen (Notes)
  // känner inte till patienten. Kopplingen sparas i patient_journal_entries
  // när journalen sparas till patient.
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
  };

  const handlePatientClear = () => {
    setSelectedPatient(null);
  };

  // ===========================================================
  //   Auto-pausa om man lämnar sidan
  // ===========================================================
  // Körs BARA när sidan lämnas. (Tidigare kördes cleanupen vid varje ändring av
  // inspelningsläget och pausade då t.ex. en start som rullades tillbaka.)
  const leavePageRef = useRef(null);
  leavePageRef.current = () => {
    if (recording.isRecording && !recording.isPaused) {
      pauseRecording();
    }
  };
  useEffect(() => {
    return () => {
      leavePageRef.current?.();
      // Rensa retry-polling vid unmount
      if (retryCleanupRef.current) {
        retryCleanupRef.current();
        retryCleanupRef.current = null;
      }
      // Släpp fliklåset (inspelningen kan inte fortsätta härifrån).
      releaseRecordingLock();
    };
  }, []);

// ===========================================================
//              Inspelningslogik
// ===========================================================
// Spärr mot dubbelstart i samma fönster (dubbelklick, dubbel trigger): bara ett
// startförsök åt gången, och knappen är inaktiv tills starten är klar.
const startRecording = async () => {
  if (startingRef.current) return;
  startingRef.current = true;
  setIsStarting(true);
  try {
    await doStartRecording();
  } finally {
    startingRef.current = false;
    setIsStarting(false);
  }
};

const doStartRecording = async () => {
  clearError();
  setShowJournal(false);
  setRecordingNotice(null);
  setLocalDeferredNotice(false);

  // Fortsätter vi en session? Bara om den är öppnad via URL:en eller hör till den
  // lokala inspelningen. Ett kvarglömt id i vyn (t.ex. via Nytt patientmöte, samma
  // route) ska ge en ny inspelning genom låset, inte spela in i den gamla sessionen.
  if (!(sessionId && (sessionIdFromURL || clientSessionId))) {
    // 0) En inspelning åt gången i den här webbläsaren: spelar en annan flik eller
    //    ett annat fönster redan in startar vi inte.
    const lockState = await acquireRecordingLock();
    if (lockState === 'busy') {
      setRecordingNotice(NOTICE_OTHER_WINDOW);
      return;
    }

    // Helt ny session - starta från början
    setSessionId(null);
    recording.start();
    resetTranscription();
    setStreamingSections([]);
    setGeneratedSections([]);

    // 1) Skapa ALLTID en lokal session först — kräver ingen server och kan aldrig
    //    misslyckas pga nedgång. Inspelningen är därmed säkrad från första sekunden.
    const chosenTemplateId = templateId || selectedTemplate?.template_id;
    let cs = null;
    try {
      cs = await beginLocalSession({ templateId: chosenTemplateId, sessionName });
      setClientSessionId(cs);
    } catch (err) {
      // IndexedDB kan i extremfall saknas — logga men fortsätt (server-create nedan).
      console.error('Kunde inte skapa lokal session:', err);
    }

    // 2) Försök skapa server-sessionen — men blockera INTE inspelningen om det
    //    misslyckas (servern kan vara nere). Chunkar buffras lokalt och server-
    //    sessionen skapas då lazy vid första lyckade upload.
    //    Skicka client_session_id så server-sessionen länkas till klientens id —
    //    annars hittar inte chunk-uploaderna sessionen och skapar en dubblett.
    //    local_client_session_ids: vi har låset, så ingen av den här webbläsarens
    //    lokala sessioner spelar in nu — servern ska inte se deras eftersläpande
    //    uppladdning som en pågående inspelning.
    let localIds = [];
    if (lockState === 'acquired') {
      try {
        localIds = (await getAllSessions()).map((s) => s.client_session_id);
      } catch (_) { /* IndexedDB kan saknas */ }
    }
    try {
      const resp = await apiClient.post('/recorder/sessions/start', {
        template_id: chosenTemplateId,
        ...(cs && { client_session_id: cs }),
        local_client_session_ids: localIds,
      });
      const newSessionId = resp.data.session_id;
      setSessionId(newSessionId);
      await attachServerSession(newSessionId);
    } catch (err) {
      if (err.response?.status === 409) {
        // Användaren spelar redan in på en annan enhet/i en annan webbläsare
        // (t.ex. desktop-appen). Rulla tillbaka: mikrofonen har inte startats.
        recording.reset();
        setClientSessionId(null);
        if (cs) deleteSessionAndChunks(cs).catch(() => {});
        releaseRecordingLock();
        setRecordingNotice(NOTICE_OTHER_DEVICE);
        return;
      }
      console.warn('Server-session kunde inte skapas vid start (fortsätter lokalt):', err);
    }
  } else {
    // Befintlig session - ska egentligen inte komma hit om användaren pausat
    // Detta kan bara hända om sidan laddats om eller liknande
    console.log('Starting with existing session - this should not happen if user paused');
    recording.start();
  }

  if (transcriptionAreaRef.current) {
    await transcriptionAreaRef.current.startRecording();
  }
};

  const pauseRecording = async () => {
    if (!recording.isRecording || recording.isPaused) return;
    
    const elapsedBeforePause = recording.getElapsedTime();
    console.log('Pausing recording with elapsed time:', elapsedBeforePause, 'ms');
    
    recording.pause();

    if (transcriptionAreaRef.current) {
      const blob = await transcriptionAreaRef.current.stopRecording();
      if (blob) {
        await handleChunk(blob, false);
      }
    }

    if (sessionId) {
      try {
        await updateSessionStatus('pause', elapsedBeforePause);
      } catch (err) {
        handleError('Kunde inte pausa session', err);
      }
    }
  };

  const resumeRecording = async () => {
    if (!recording.isPaused) return;

    // Samma regel som vid start: spelar en annan flik redan in återupptar vi inte.
    if (await acquireRecordingLock() === 'busy') {
      setRecordingNotice(NOTICE_OTHER_WINDOW);
      return;
    }
    setRecordingNotice(null);

    recording.resume();

    if (transcriptionAreaRef.current) {
      transcriptionAreaRef.current.startRecording();
    }

    if (sessionId) {
      try {
        await updateSessionStatus('resume');
      } catch (err) {
        handleError('Kunde inte återuppta session', err);
      }
    }
  };


  const stopRecording = async () => {
    if (isStopping) {
      console.log('Already stopping, ignoring duplicate call');
      return;
    }
    // Starten pågår fortfarande (mikrofonen öppnas efter serversvaret). Avslutas
    // den nu skulle mikrofonen öppnas efter avslutet och låset redan vara släppt.
    if (startingRef.current) return;

    setIsStopping(true);
    recording.stop();

    if (!selectedTemplate) {
      alert('Du måste välja en mall innan du avslutar inspelningen.');
      setIsStopping(false);
      return;
    }

    // 1) Sista chunk → lokal kö (markera final för finish-prioritet server-sidan)
    if (transcriptionAreaRef.current) {
      const blob = await transcriptionAreaRef.current.stopRecording();
      if (blob) {
        await handleChunk(blob, true);
      }
    }

    // 2) Markera klar lokalt (endast lokal-först-sessioner). Uppladdaren kör
    //    server-`complete` när den lokala kön är tömd (så servern aldrig avslutar
    //    med ett hål i transkriptet). Återupptagna legacy-sessioner completas nedan.
    if (clientSessionId) {
      await finishLocalSession();
    }
    // Inspelningen är slut → andra fönster får starta en ny.
    releaseRecordingLock();

    // 3) Registrera varaktighet/complete server-sidan om sessionen redan finns.
    //    Sena chunkar accepteras på en completed session. Saknas sessionen (server
    //    var nere vid start) sköter uppladdaren complete lazy när den kommit upp.
    if (sessionId) {
      try {
        await updateSessionStatus('complete', recording.getElapsedTime());
      } catch (err) {
        console.warn('complete_session kunde inte köras nu (uppladdaren gör det senare):', err);
      }
    }

    // 4) Vänta in lokal kö + server-processering i pending-modalen. Journalen
    //    genereras först när ALLT bekräftats (transcriptionRef är då komplett).
    setShowPendingModal(true);
  };

  // Helper-funktion för journal-generering
  const proceedWithJournalGeneration = async () => {
    // Visa loading overlay
    setShowGenerationOverlay(true);
    setGenerationMessage('Aion genererar din journal...');

    // Visa JournalEditor direkt
    setShowJournal(true);

    // Kör GPT-generering (stream i realtid)
    try {
      const finalGPTtext = await generateJournalStreaming(
        transcriptionRef.current.trim(),
        selectedTemplateRef.current?.template_id ?? selectedTemplate?.template_id
      );
      if (finalGPTtext) {
        await saveGeneratedJournal(finalGPTtext);
      }
    } catch (err) {
      handleError('Fel vid GPT-generering efter stopp', err);
    } finally {
      setIsStopping(false);
    }
  };

  // Callback när pending modal är klar
  const handlePendingComplete = async (result) => {
    console.log('Active chunks completed, proceeding with journal generation', result);
    setShowPendingModal(false);

    // Servern var ihållande nere: lokala chunkar väntar fortfarande. Generera
    // INTE en halv journal — inspelningen är säkrad lokalt och skickas automatiskt
    // när servern är tillbaka. Visa lugnande info i stället.
    if (result?.deferredLocal) {
      setLocalDeferredNotice(true);
      setIsStopping(false);
      // Inspelningen är avslutad och levereras av uppladdaren. Nollställ vyn, annars
      // hamnar nästa patients ljud på den här sessionen.
      setClientSessionId(null);
      setSessionId(null);
      recording.reset();
      return;
    }

    // Om det finns retries i bakgrunden - starta polling
    if (result?.hasMorePending && sessionId) {
      setBackgroundRetryActive(true);

      // Rensa eventuell tidigare polling
      if (retryCleanupRef.current) retryCleanupRef.current();

      retryCleanupRef.current = subscribeToSessionStatus(sessionId, (status) => {
        if (!status.has_pending_retries) {
          setBackgroundRetryActive(false);
          console.log('Background retries completed, refreshing session data');
          fetchSessionData(sessionId);
          if (retryCleanupRef.current) {
            retryCleanupRef.current();
            retryCleanupRef.current = null;
          }
        }
      });
    }

    await proceedWithJournalGeneration();
  };


  // ===========================================================
  //         Fake UX Streaming Helper
  // ===========================================================
  const animateSectionsAppearance = async (sections, setSections) => {
    /**
     * Visar sektioner en i taget med delay för bättre UX
     * Även om backend returnerar allt på en gång
     */
    const DELAY_BETWEEN_SECTIONS = 400; // ms mellan varje sektion

    for (let i = 0; i < sections.length; i++) {
      // Visa sektioner progressivt
      setSections(sections.slice(0, i + 1));

      // Vänta innan nästa sektion (skippa delay efter sista)
      if (i < sections.length - 1) {
        await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_SECTIONS));
      }
    }
  };

  // ===========================================================
  //         parseJournalNote (SIMPLIFIED - trusts backend)
  // ===========================================================
  const parseJournalNote = (text, template = null) => {
    /**
     * @param template - Optional template override (fixes stale closure when switching templates)
     */

    if (!text || !text.trim()) {
      return [];
    }

    const templateToUse = template || selectedTemplate;

    // Hämta tillåtna rubriker från template (för parsing)
    const allowedTitles = new Set();
    if (templateToUse?.sections) {
      templateToUse.sections.forEach(sec => {
        allowedTitles.add(sec.section_title.trim());
      });
    }

    const lines = text.split('\n');
    const secs = [];
    let current = null;
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();

      if (line) {
        // Är detta en giltig rubrik?
        const isValidTitle = allowedTitles.size === 0 || allowedTitles.has(line);

        if (isValidTitle && !current) {
          // Starta ny sektion
          current = { title: line, content: '' };
          i++;

          // Hoppa över tomma rader efter rubrik
          while (i < lines.length && lines[i]?.trim() === '') i++;

          // Samla innehåll tills nästa rubrik eller tom rad
          while (i < lines.length) {
            const nextLine = lines[i].trim();

            // Ny rubrik? Avbryt (konsekvent med isValidTitle-logiken ovan)
            const isNextValidTitle = allowedTitles.size === 0 || allowedTitles.has(nextLine);
            if (nextLine && isNextValidTitle) {
              break;
            }

            // Tom rad efter innehåll? Avbryt
            if (!nextLine && current.content.trim()) {
              i++;
              break;
            }

            current.content += lines[i] + '\n';
            i++;
          }

          secs.push(current);
          current = null;
        } else if (current) {
          // Lägg till i nuvarande sektion
          current.content += line + '\n';
          i++;
        } else {
          // Text utan rubrik - lägg i "Okänt" eller senaste sektion
          if (secs.length === 0) {
            secs.push({ title: 'Okänt', content: line + '\n' });
          } else {
            secs[secs.length - 1].content += line + '\n';
          }
          i++;
        }
      } else {
        i++;
      }
    }

    return secs;
  };

  const generateJournalStreaming = async (text, templateId, templateOverride = null) => {
    /**
     * SIMPLIFIED med FAKE UX STREAMING
     * Backend returnerar korrekt data direkt
     * Frontend visar sektioner progressivt för bättre UX
     *
     * @param templateOverride - Optional template object to use for parsing (fixes stale closure on template switch)
     */

    if (!templateId) {
      handleError('Ingen mall vald');
      return '';
    }

    // Härled ALLTID parsnings-mallen från samma template_id som skickas till
    // backend. Annars kan backend generera mot en mall medan frontend parsar
    // mot en annan (stale selectedTemplate) → rubriker matchar inte och viks
    // ihop under en rubrik.
    const templateForParse =
      templateOverride ||
      templates.find((t) => t.template_id === templateId) ||
      selectedTemplateRef.current ||
      selectedTemplate;

    console.log('[generateJournal] template_id sent:', templateId,
      '| parse-mall:', templateForParse?.template_name);

    setStreamingSections([]);
    setGeneratedSections([]);
    setIsGenerating(true);
    clearError();

    let accumulated = '';

    try {
      // Backend streaming (samlar hela svaret)
      await streamClient.post(
        '/api/chatgpt/generate_journal',
        { text, template_id: templateId, session_id: sessionId },
        (chunk) => {
          accumulated += chunk;
          const partial = parseJournalNote(accumulated, templateForParse);
          setStreamingSections(partial);
        }
      );

      // Parse final result
      const finalSecs = parseJournalNote(accumulated.trim(), templateForParse);

      // FAKE UX STREAMING: Visa sektioner en i taget
      setStreamingSections([]);
      await animateSectionsAppearance(finalSecs, setStreamingSections);

      // När animationen är klar
      setGeneratedSections(finalSecs);
      setStreamingSections([]);
      setIsGenerating(false);

      const gptText = finalSecs
        .map((sec) => `${sec.title}\n${sec.content.trim()}`)
        .join('\n\n');

      return gptText;
    } catch (err) {
      handleError('Fel vid GPT-generering', err);
      setIsGenerating(false);
      return '';
    } finally {
      // Dölj loading overlay när genereringen är klar
      setShowGenerationOverlay(false);
    }
  };

  // ===========================================================
  //       JournalEditor-callbacks
  // ===========================================================
  const handleRegenerate = async () => {
    const combinedText = transcriptionRef.current.trim();

    // generateJournalStreaming hanterar nu fake UX streaming internt.
    // Läs mall-id från ref:en (alltid färsk) så att en regenerering direkt
    // efter ett mallbyte inte skickar gammalt template_id.
    const gptText = await generateJournalStreaming(
      combinedText,
      selectedTemplateRef.current?.template_id ?? selectedTemplate?.template_id
    );

    if (gptText && sessionId) {
      await saveGeneratedJournal(gptText);
    }
  };

  const handleUpdateGeneratedSections = (newSections) => {
    setGeneratedSections(newSections);
  };

  const handleChangeTemplate = async (newTemplateId) => {
    const found = templates.find((t) => t.template_id === newTemplateId);
    if (!found) {
      handleError('Mall ej hittad.');
      return;
    }

    // Rensa gamla sektioner INNAN mallbyte, annars kör useJournalSections
    // prepareSections(gamla sektioner, ny mall) och manglar rubrikerna
    setGeneratedSections([]);
    // Synkron uppdatering av ref:en så att ev. regenerering som triggas innan
    // setSelectedTemplate hunnit commit:a ändå läser den NYA mallen (annars
    // skickas gamla template_id till generate_journal → fel mall genereras).
    selectedTemplateRef.current = found;
    setSelectedTemplate(found);
    setTemplateId(newTemplateId);

    if (sessionId) {
       await apiClient.patch(
         `/recorder/sessions/${sessionId}/update_template`,
         { template_id: newTemplateId }
       );
    }
    // Endast om vi är i Journal-läge vill vi regenerera. Vi gör det INTE direkt
    // här (då hinner inte selectedTemplate uppdateras → fel mall genereras).
    // I stället flaggar vi för regenerering som körs i effekten nedan när
    // selectedTemplate faktiskt blivit den nya mallen — exakt som att manuellt
    // trycka "Gör om journal" efter bytet.
    if (showJournal) {
      setRegenerateForTemplateId(newTemplateId);
    }
  };

  // Kör regenereringen efter mallbyte – först när selectedTemplate hunnit bli
  // den nya mallen. Då använder handleRegenerate rätt mall (samma som knappen).
  useEffect(() => {
    if (regenerateForTemplateId == null) return;
    if (selectedTemplate?.template_id !== regenerateForTemplateId) return;
    setRegenerateForTemplateId(null);
    handleRegenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenerateForTemplateId, selectedTemplate]);

  const handleAdjust = async (instruction) => {
    if (generatedSections.length === 0) {
      handleError('Inga sektioner att justera.');
      return;
    }
    try {
      const resp = await apiClient.post('/api/journal/adjust', {
        instruction,
        sections: generatedSections.map((sec) => ({
          title: sec.title,
          content: sec.content,
        })),
      });
      if (resp.data.sections && resp.data.sections.length > 0) {
        setGeneratedSections(resp.data.sections);

        const combinedText = resp.data.sections
          .map((s) => `${s.title}\n${s.content.trim()}`)
          .join('\n\n');

        if (sessionId) {
          await saveGeneratedJournal(combinedText);
        }
      } else {
        handleError('Justering returnerade inga sektioner.');
      }
    } catch (err) {
      handleError('Fel vid justering av journal', err);
    }
  };


  // ===========================================================
  //                Render
  // ===========================================================

  // 1) Om showJournal => visa JournalEditor
  if (showJournal) {
    return (
      <JournalEditor
        sessionId={sessionId}
        inputText={transcriptionRef.current}
        selectedTemplate={selectedTemplate}
        templates={templates}
        streamingSections={streamingSections}
        generatedSections={generatedSections}
        isTranscribing={isGenerating}
        onRegenerate={handleRegenerate}
        onChangeTemplate={handleChangeTemplate}
        onAdjust={handleAdjust}
        onUpdateGeneratedSections={handleUpdateGeneratedSections}
        startDateTime={recording.startDateTime}
        sessionName={sessionName}
        onSessionNameChange={setSessionName}
        // Props för patientjournal-integration
        patientId={selectedPatient?.patient_id || null}
        selectedPatient={selectedPatient}
        onPatientChange={setSelectedPatient}
        appointmentId={linkedAppointmentId}
        useInternalJournal={clinicSettings.use_internal_journal}
        transcriptionUpdatedAfterJournal={transcriptionUpdatedAfterJournal}
        onJournalSaved={(entryId) => {
          console.log('Journal saved with entry_id:', entryId);
          // Om vi kom från patientvyn - navigera tillbaka till patientens journal
          if (selectedPatient?.patient_id) {
            navigate(`/patients/${selectedPatient.patient_id}?tab=journal`);
          }
        }}
      />
    );
  }

  // 2) Annars inspelningsvyn
return (
  <>
    <TopBar
      templates={templates}
      selectedTemplate={selectedTemplate}
      loadingTemplates={loadingTemplates}
      onTemplateChange={handleChangeTemplate}
      showTabs={false}
      showStopButton={(recording.isRecording || recording.isPaused) && !isStarting}
      onStopRecording={stopRecording}
      startDateTime={recording.startDateTime}
      microphones={microphones}
      selectedMicId={selectedMicId}
      onChangeMic={(micId) => setSelectedMicId(micId)}
      captureSystemAudio={captureSystemAudio}
      onToggleSystemAudio={() => setCaptureSystemAudio((prev) => !prev)}
      hasRecordingError={!!error}
    />

    <div className="main-content-container">
      <div className="main-content">
        <div className="recording-wrapper">
          {/* Session Name Field - visible when session exists, above header */}
          {sessionId && (
            <SessionNameField
              sessionId={sessionId}
              initialName={sessionName}
              onNameChange={setSessionName}
              placeholder="Namnge journalen..."
              className="main-content-session-name"
            />
          )}

          <h2 className="main-header">Nytt Patientsamtal</h2>

          {/* Patientväljare - visas endast om kliniken använder inbyggd journal */}
          {clinicSettings.use_internal_journal && (
            <div className="main-content-patient-selector">
              <div className="patient-selector-wrapper">
                <label>Koppla till patient (valfritt)</label>
                <PatientSelector
                  selectedPatient={selectedPatient}
                  onPatientSelect={handlePatientSelect}
                  onClear={handlePatientClear}
                  placeholder="Sök patient..."
                  disabled={false}
                />
              </div>
            </div>
          )}

          {recording.startDateTime && (
            <div className="start-date-time">
              Startat: {formatDateTime(recording.startDateTime)}
            </div>
          )}

          <Timer currentTime={recording.currentTime} />

          <div className="central-record-button-container">
            <button
              className={`central-record-button elongated ${recording.isRecording ? 'recording' : ''}`}
              style={{ '--sound-level': soundLevel }}
              disabled={isStarting || recordingElsewhere}
              onClick={() => {
                if (!recording.isRecording && !recording.isPaused) {
                  startRecording();
                } else if (recording.isRecording && !recording.isPaused) {
                  pauseRecording();
                } else if (recording.isPaused) {
                  resumeRecording();
                }
              }}
            >
              {!recording.isRecording && !recording.isPaused
                ? 'Starta inspelning'
                : recording.isRecording && !recording.isPaused
                ? 'Pausa inspelning'
                : 'Återuppta inspelning'}
            </button>

            {recording.isRecording && !recording.isPaused && noAudioDetected && (
              <div className="no-audio-warning" role="status">
                Inget ljud detekteras — talar du, och är rätt mikrofon vald?
              </div>
            )}

            {recordingNotice && (
              <div
                className="alert alert-warning mb-0 py-2 px-3 recording-notice"
                role="status"
                style={{
                  position: 'absolute',
                  top: 'calc(50% + 55px)',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 'max-content',
                  maxWidth: 'min(380px, 90vw)',
                  fontSize: '0.9rem',
                  zIndex: 3,
                }}
              >
                {recordingNotice}
              </div>
            )}
          </div>

          {/* Humla Image Gallery Button */}
          {sessionId && (
            <div className="image-gallery-button-container" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => setShowImageGallery(true)}
                title="Visa bilder från Humla"
              >
                🖼️ Bilder från Humla
              </button>
            </div>
          )}
        </div>

        {/* Om du vill visa rullande transkriberings-text i UI, avkommentera detta block:
        <div className="transcription-preview" style={{ marginTop: '2rem', padding: '1rem', background: '#f7f7f7' }}>
          <h3>Rullande transkribering (chunk-baserad):</h3>
          {isTranscribing && <p style={{ color: 'gray' }}>Transkriberar chunk...</p>}
          {transcription ? (
            <p>{transcription}</p>
          ) : (
            <p style={{ fontStyle: 'italic', color: '#666' }}>
              Ingen transkription hittills. Prata för att se text här.
            </p>
          )}
        </div>
        */}

        <div style={{ display: 'none' }}>
          <TranscriptionArea
            ref={transcriptionAreaRef}
            onDataAvailable={(chunk) => handleChunk(chunk, false)}
            selectedMicId={selectedMicId}
            captureSystemAudio={captureSystemAudio}
          />
        </div>

        {error && <p className="error-message">{error}</p>}

        {backgroundRetryActive && (
          <div className="alert alert-info mt-2 mb-0 py-2 px-3" style={{ fontSize: '0.85rem' }}>
            Vissa delar av inspelningen transkriberas fortfarande i bakgrunden. Journalen uppdateras automatiskt.
          </div>
        )}

        {localDeferredNotice && (
          <div className="alert alert-warning mt-2 mb-0 py-2 px-3" style={{ fontSize: '0.85rem' }}>
            Din inspelning är inte riktigt färdig ännu. Den blir klar så snart som
            möjligt. Du hittar den sedan under Mina patientmöten och kan skapa journalen där.
          </div>
        )}
      </div>
    </div>

    {/* Loading overlay för journal generation */}
    {showGenerationOverlay && (
      <div className="journal-generation-overlay">
        <div className="loading-spinner"></div>
        <p className="loading-text">{generationMessage}</p>
      </div>
    )}

    {/* Humla Image Gallery */}
    <ImageGallery
      isOpen={showImageGallery}
      onClose={() => setShowImageGallery(false)}
      sessionId={sessionId}
      autoRefresh={false}
    />

    {/* Pending Transcription Modal */}
    {showPendingModal && (
      <TranscriptionPendingModal
        sessionId={sessionId}
        localPending={localPendingForSession}
        onComplete={handlePendingComplete}
      />
    )}
  </>
);
}

export default MainContent;
